import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PreviewBanner } from "../PreviewBanner";
import { OutcomeTile } from "../OutcomeTile";
import { EvidenceChip } from "../EvidenceChip";
import { QSection, Prose, Unavailable, Method } from "./Bits";
import { Explorer } from "./Explorer";
import { SubjectSwitcher } from "./Lite";

const QS = [["what", "What is it?"], ["who", "Is it for me?"], ["study", "What will I study?"], ["admissions", "How do I get in?"],
  ["careers", "Where does it lead?"], ["earnings", "What do graduates earn?"], ["value", "Opportunities and value"], ["where", "Where should I study it?"]];
const gbp = (v) => `£${Math.round(v).toLocaleString()}`;

const List = ({ rows, prefix, className }) => rows.length
  ? <div className={className || "space-y-3"}>{rows.map((it, i) => <Prose key={i} item={it} testId={`${prefix}-${i}`} />)}</div>
  : <Unavailable testId={`${prefix}-none`}>Not verified yet.</Unavailable>;

// Optional schema-2 copy: section context paragraphs and cited key facts.
const Extra = ({ e, id }) => {
  const ctx = e[`${id}_context`] || [];
  const facts = e.key_facts?.[id] || [];
  if (!ctx.length && !facts.length) return null;
  return (
    <div className="mt-8 space-y-4" data-testid={`sci-guide-extra-${id}`}>
      {ctx.map((it, i) => <Prose key={i} item={it} testId={`sci-guide-${id}-context-${i}`} />)}
      {!!facts.length && <ul className="list-disc space-y-2 pl-5" data-testid={`sci-guide-${id}-facts`}>{facts.map((it, i) => (
        <li key={i}><Prose item={it} as="span" testId={`sci-guide-${id}-fact-${i}`} /></li>))}</ul>}
    </div>
  );
};

function DraftQA({ qa }) {
  if (!qa) return null;
  if (qa.schema === 1) return <p data-testid="sci-guide-qa" className="mt-4 rounded-lg border border-stone-300 bg-white p-3 text-xs text-stone-700">No imported ChatGPT draft for this subject yet; showing the current copy.</p>;
  return (
    <details data-testid="sci-guide-qa" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950" open={qa.withheld.length > 0}>
      <summary className="cursor-pointer font-medium" data-testid="sci-guide-qa-summary">
        {qa.mode === "draft" ? "Editorial draft" : "Imported copy"} · status {qa.status || "unknown"} · {qa.writer || "unknown writer"} · {qa.withheld.length} items withheld
      </summary>
      <ul className="mt-2 space-y-2">{qa.withheld.map((w) => (
        <li key={w.id} data-testid={`sci-guide-qa-withheld-${w.id}`}><span className="font-mono">{w.id}</span>: {w.text}<br /><span className="text-amber-800">{w.reasons.join("; ")}</span></li>))}</ul>
    </details>
  );
}

const Range = ({ label, q, fmt, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-stone-200 bg-white p-4">
    <p className="text-xs text-stone-600">{label}</p>
    {q ? <><p className="mt-1 font-mono text-2xl text-stone-900">{fmt(q.median)}</p>
      <p className="text-[11px] text-stone-500">Middle half of courses: {fmt(q.p25)} to {fmt(q.p75)} · {q.courses} courses</p></>
      : <p className="mt-1 text-sm italic text-stone-500">Not enough data</p>}
  </div>
);

function Hero({ d }) {
  const e = d.editorial;
  return (
    <header className="py-12 lg:py-20" data-testid="sci-guide-hero">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#92400E]">Subject guide{d.subject.family ? ` · ${d.subject.family}` : ""}</p>
      <h1 className="mt-3 font-editorial text-5xl text-stone-900 sm:text-6xl lg:text-7xl" data-testid="sci-subject-title">{d.subject.name}</h1>
      {e.hero_lede[0] && <Prose item={e.hero_lede[0]} testId="sci-guide-lede" className="mt-6 max-w-3xl font-editorial text-xl leading-relaxed text-stone-700 md:text-2xl" />}
      <nav className="mt-8 flex flex-wrap gap-2" data-testid="sci-question-nav">{QS.map(([id, l], i) => (
        <a key={id} href={`#${id}`} data-testid={`sci-question-nav-${id}`} className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:border-[#002147]">
          <span className="mr-1 font-mono text-[10px] text-[#92400E]">{i + 1}</span>{l}</a>))}</nav>
    </header>
  );
}

const groupOf = (pop = "") => (pop.match(/all full-time (.+?) graduates/i) || [])[1];

const NationalTiles = ({ d }) => (
  <div className="grid gap-3 sm:grid-cols-3">{d.national_outcomes.map((c, i) => <OutcomeTile key={i} o={c} testId={`sci-guide-national-${i}`} />)}</div>
);

// Secondary comparison: one Discover Uni figure for all UK graduates in a subject group, not a spread across courses.
function NationalComparison({ d }) {
  if (!d.national_outcomes.length) return null;
  const wider = d.national_outcomes.some((c) => c.population_mismatch);
  const group = groupOf(d.national_outcomes[0].population) || "this subject area";
  const note = `One national figure per measure for all full-time UK ${group} graduates, from different graduating cohorts (see each tile). Not a spread across courses, so don't compare it directly with the figures above.`;
  if (!wider) return (
    <div data-testid="sci-guide-national"><h3 className="mb-1 text-sm font-semibold text-stone-900">For comparison: all UK {group} graduates</h3>
      <p className="mb-3 text-xs text-stone-600" data-testid="sci-guide-national-scope">{note}</p><NationalTiles d={d} /></div>
  );
  return (
    <details data-testid="sci-guide-national" className="group rounded-xl border border-stone-200 bg-white/60 p-4">
      <summary className="cursor-pointer text-sm font-semibold text-stone-900" data-testid="sci-guide-national-toggle">
        Wider comparison: all UK {group} graduates <span className="font-normal text-stone-600">(not {d.subject.name} alone)</span></summary>
      <p className="mb-3 mt-2 text-xs text-amber-900" data-testid="sci-guide-national-scope">Discover Uni only publishes these for the wider {group} group, which includes subjects other than {d.subject.name}. {note}</p>
      <NationalTiles d={d} />
    </details>
  );
}

function Earnings({ d }) {
  const o = d.facts.outcomes;
  return (
    <div className="space-y-10">
      <div data-testid="sci-guide-earnings-primary"><h3 className="mb-1 text-sm font-semibold text-stone-900">UK single-honours {d.subject.name} courses</h3>
        <p className="mb-3 text-xs text-stone-600">The middle value across courses, with the middle half of courses shown beneath.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Range label="Salary 15 months after graduating" q={o.go_salary} fmt={gbp} testId="sci-guide-go" />
          <Range label="Earnings 5 years after graduating" q={o.leo5} fmt={gbp} testId="sci-guide-leo5" />
          <Range label="In work or further study (15 months)" q={o.work_study} fmt={(v) => `${Math.round(v)}%`} testId="sci-guide-workstudy" />
        </div>
        <Method testId="sci-guide-earnings-method">{d.facts.scope}. {d.facts.source}. Minimum samples: {d.facts.min_samples}. Salary and work or study at 15 months come from the Graduate Outcomes survey; earnings at 5 years come from LEO (tax records) for a different, earlier cohort and cover the university's subject group rather than the course alone.</Method>
      </div>
      <NationalComparison d={d} />
    </div>
  );
}

const Share = ({ label, v, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-stone-200 bg-white p-4">
    <p className="text-xs text-stone-600">{label}</p>
    <p className="mt-1 font-mono text-2xl text-stone-900">{v == null ? "Not available" : `${v}%`}</p>
    <p className="text-[11px] text-stone-500">Official course flag (KIS)</p>
  </div>
);

function Value({ d }) {
  const f = d.facts;
  return (
    <div className="space-y-8">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Share label="Single-honours courses offering a year abroad" v={f.opportunities.year_abroad_pct} testId="sci-guide-abroad" />
        <Share label="Single-honours courses offering a placement year" v={f.opportunities.placement_pct} testId="sci-guide-placement" />
        <Range label="Student satisfaction (NSS average)" q={f.teaching.nss_avg} fmt={(v) => `${Math.round(v)}%`} testId="sci-guide-nss" />
        <Range label="Students continuing into year 2" q={f.teaching.continuation} fmt={(v) => `${Math.round(v * 100)}%`} testId="sci-guide-continuation" />
      </div>
      <Unavailable testId="sci-guide-value-gap">Fees, living costs and a value-for-money measure aren't verified for this subject yet.</Unavailable>
    </div>
  );
}

export function SciSubjectGuide({ d }) {
  const e = d.editorial;
  const a = d.facts.admissions;
  return (
    <div className="bg-[#FDFBF7]" data-testid="sci-subject-guide">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>{`Studying ${d.subject.name} · private preview`}</title></Helmet>
      <PreviewBanner runId={d.run_ids.join(" + ")} />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SubjectSwitcher current={d.subject.slug} />
        <DraftQA qa={d.editorial_qa} />
        <Hero d={d} />
        <QSection id="what" n={1} title={`What is ${d.subject.name}?`}>
          <List rows={e.what_intro} prefix="sci-guide-what" className="grid gap-x-12 gap-y-5 md:grid-cols-2" />
          {!!e.questions.length && <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="sci-guide-questions">{e.questions.map((it, i) => (
            <li key={i} className="rounded-xl bg-[#002147] p-5 text-white"><Prose item={it} testId={`sci-guide-q-${i}`} className="font-editorial text-xl leading-snug [&_button]:text-white/60" /></li>))}</ul>}
          <Extra e={e} id="what" />
        </QSection>
        <QSection id="who" n={2} title="Is it for me?" lede="The interests and strengths this subject rewards. Your personal course fit is in the explorer below.">
          <div className="grid gap-10 lg:grid-cols-2">
            <div><h3 className="mb-3 text-sm font-semibold text-stone-900">You'll probably enjoy it if…</h3><List rows={e.who_traits} prefix="sci-guide-trait" /></div>
            <div><h3 className="mb-3 text-sm font-semibold text-stone-900">What the degree trains you to do</h3><List rows={e.who_skills} prefix="sci-guide-skill" /></div>
          </div>
          <p className="mt-6 text-sm text-stone-700">Not sure yet? <Link to="/beta/course-fit" data-testid="sci-guide-fast-match" className="font-medium text-[#002147] underline">Take Fast Match</Link> or <Link to="/profile" data-testid="sci-guide-profile" className="font-medium text-[#002147] underline">update your Profile</Link>.</p>
          <Extra e={e} id="who" />
        </QSection>
        <QSection id="study" n={3} title="What will I study?" lede="Areas shared across UK degrees, from the national subject benchmark. Each university's modules differ.">
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{d.topic_areas.map((c, i) => (
            <li key={i} data-testid={`sci-guide-topic-${i}`} className="flex items-start justify-between gap-2 rounded-xl border border-stone-200 bg-white p-3 text-sm text-stone-800">
              <span>{c.value.charAt(0).toUpperCase() + c.value.slice(1)}</span><EvidenceChip claim={c} subtle testId={`sci-guide-topic-${i}-source`} /></li>))}</ul>
          <div className="mt-10 grid gap-10 md:grid-cols-2">
            <div><h3 className="mb-3 text-sm font-semibold text-stone-900">How it's taught</h3><List rows={e.study_teaching} prefix="sci-guide-teach" /></div>
            <div><h3 className="mb-3 text-sm font-semibold text-stone-900">How it's assessed</h3><List rows={e.study_assessment} prefix="sci-guide-assess" /></div>
          </div>
          <Extra e={e} id="study" />
          <div className="mt-6"><Unavailable testId="sci-guide-modules-gap">{d.gaps[0]}.</Unavailable></div>
        </QSection>
        <QSection id="admissions" n={4} title="How do I get in?">
          <div className="grid items-start gap-8 md:grid-cols-2">
            <table className="w-full text-sm" data-testid="sci-guide-offers"><caption className="mb-2 text-left text-sm font-semibold text-stone-900">Most common typical A-level offers</caption>
              <tbody>{a.typical_a_level.map((r) => <tr key={r.offer} className="border-b border-stone-100"><td className="py-1.5 font-mono">{r.offer}</td><td className="py-1.5 text-right text-stone-600">{r.courses} courses</td></tr>)}</tbody></table>
            <Range label="Typical UCAS tariff points of entrants" q={a.tariff} fmt={(v) => Math.round(v)} testId="sci-guide-tariff" />
          </div>
          <Method testId="sci-guide-admissions-method">{a.note}. {d.facts.source}. Offers vary by course; always check the university's own page.</Method>
          <Extra e={e} id="admissions" />
        </QSection>
        <QSection id="careers" n={5} title="Where does it lead?">
          <List rows={e.careers_national} prefix="sci-guide-career" className="grid gap-x-12 gap-y-4 md:grid-cols-2" />
          <Extra e={e} id="careers" />
          {!e.careers_national.some((r) => /employer/i.test(r.text)) && <div className="mt-6"><Unavailable testId="sci-guide-employers-gap">{d.gaps[2]}.</Unavailable></div>}
        </QSection>
        <QSection id="earnings" n={6} title="What do graduates earn?"><Earnings d={d} /><Extra e={e} id="earnings" /></QSection>
        <QSection id="value" n={7} title="Opportunities and value"><Value d={d} /><Extra e={e} id="value" /></QSection>
        <QSection id="where" n={8} title={`Where should I study ${d.subject.name}?`} lede={d.ranking_note}><Extra e={e} id="where" /><Explorer slug={d.subject.slug} /></QSection>
      </div>
    </div>
  );
}
